/**
 * billingAdmin.service.ts
 *
 * Typed API wrappers for the admin "Account > Billing" page
 * (`/admin/billing-methods`). Backs SubscribeBillingController on the API
 * side, which talks to legacy v4 `subscribe_*` tables and the Forward v4
 * proxy. JWT is injected by the shared `apiClient` interceptor.
 */
import { apiClient } from './apiClient';
import type { ApiResponse } from '@/types/api.types';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface SubscribeBillingSubscription {
  subscription_id: number;
  facility_id: number;
  plan_id: number;
  plan_name: string;
  base_price_cents: number;
  billing_interval: string;
  status: string;
  current_period_start: string | null;
  current_period_end: string | null;
  last_charge_at: string | null;
  next_charge_at: string | null;
}

export interface SubscribeBillingPaymentMethod {
  pm_id: number;
  facility_id: number;
  gateway: string;
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
  billing_name: string;
  billing_zip: string;
  is_default: boolean;
  created_at: string | null;
  updated_at: string | null;
  deleted_at: string | null;
}

export interface SubscribeBillingInvoice {
  invoice_id: number;
  invoice_number: string;
  subscription_id: number;
  period_start: string | null;
  period_end: string | null;
  subtotal_cents: number;
  tax_cents: number;
  total_cents: number;
  /** Total refunded against this invoice, in cents (0 when none). */
  refunded_cents: number;
  /** True when any part of this invoice's charge has been refunded. */
  is_refunded: boolean;
  status: string;
  issued_at: string | null;
  due_at: string | null;
  paid_at: string | null;
}

export interface SubscribeBillingPlan {
  plan_id: number;
  name: string;
  base_price_cents: number;
  billing_interval: string;
  currency: string;
  is_active: boolean;
}

export interface SubscribeBillingFacility {
  facilityID: number;
  name: string;
}

export interface SubscribeBillingMerchant {
  subscribe_merchant_id: number;
  forward_account_id: string;
  public_api_key: string;
  friendly_title: string;
}

export interface SubscribeBillingOverview {
  subscriptions: SubscribeBillingSubscription[];
  payment_methods: SubscribeBillingPaymentMethod[];
  recent_invoices: SubscribeBillingInvoice[];
  plans: SubscribeBillingPlan[];
  facilities: SubscribeBillingFacility[];
  merchant: SubscribeBillingMerchant | null;
}

export interface SubscribeBillingIntent {
  client_secret: string | null;
  intent_id: string | null;
  public_api_key: string;
  merchant_id: number;
  forward_account_id: string;
}

export interface SaveSubscribePaymentMethodInput {
  token: string;
  intent_id?: string | null;
  billing_name?: string;
  facilityID?: number;
  cardType?: string;
  lastfour?: string;
  exp_month?: number;
  exp_year?: number;
}

// ─── API calls ──────────────────────────────────────────────────────────────

export function getBillingOverview(): Promise<SubscribeBillingOverview> {
  return apiClient
    .get<ApiResponse<SubscribeBillingOverview>>('/admin/billing-methods')
    .then((r) => r.data.data);
}

export function createSubscribeIntent(): Promise<SubscribeBillingIntent> {
  return apiClient
    .post<ApiResponse<SubscribeBillingIntent>>('/admin/billing-methods/intents', {})
    .then((r) => r.data.data);
}

export function saveSubscribePaymentMethod(
  input: SaveSubscribePaymentMethodInput,
): Promise<SubscribeBillingPaymentMethod> {
  return apiClient
    .post<ApiResponse<SubscribeBillingPaymentMethod>>(
      '/admin/billing-methods/payment-methods',
      input,
    )
    .then((r) => r.data.data);
}

export function updatePaymentMethodFacility(
  pmId: number,
  facilityID: number,
): Promise<SubscribeBillingPaymentMethod> {
  return apiClient
    .patch<ApiResponse<SubscribeBillingPaymentMethod>>(
      `/admin/billing-methods/payment-methods/${pmId}/facility`,
      { facilityID },
    )
    .then((r) => r.data.data);
}

export function setDefaultPaymentMethod(
  pmId: number,
): Promise<SubscribeBillingPaymentMethod> {
  return apiClient
    .patch<ApiResponse<SubscribeBillingPaymentMethod>>(
      `/admin/billing-methods/payment-methods/${pmId}/default`,
      {},
    )
    .then((r) => r.data.data);
}

export function deletePaymentMethod(
  pmId: number,
): Promise<{ pm_id: number; deleted_at: string }> {
  return apiClient
    .delete<ApiResponse<{ pm_id: number; deleted_at: string }>>(
      `/admin/billing-methods/payment-methods/${pmId}`,
    )
    .then((r) => r.data.data);
}
